<script setup lang="ts">
import { getSkillName, skillCollection, getSkillNameById } from '@/core/skill'
import { useUserStore } from '@/stores/modules/user'
import type { SkillItemType } from '@/types/skill'
import { computed, ref } from 'vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import SkillInfo from '@/views/item/components/SkillInfo.vue'
import { socketEmitAsync } from '@/utils/web-socket'
import { SynthesisSkillNeedFragmentNumEnum } from '@/enums'

const userStore = useUserStore()

const skillItemList = ref<SkillItemType[]>([])
function getAllSkillItem() {
  skillItemList.value = Object.entries(skillCollection).map(([id, item]) => {
    return {
      id,
      skillId: Number(id),
      quality: item.quality[item.quality.length - 1]!,
      attrs: [],
      isLock: false,
    }
  })
}
getAllSkillItem()

const skillInfo = ref<SkillItemType>()
function showDetail(item: SkillItemType) {
  skillInfo.value = item
}
const needFragmentCount = computed(() => {
  if (!skillInfo.value) return 0
  return SynthesisSkillNeedFragmentNumEnum[skillInfo.value.quality as keyof typeof SynthesisSkillNeedFragmentNumEnum]
})
const synthesisResShow = ref(false)
const synthesisSkillInfo = ref<SkillItemType>()
function handleSynthesis() {
  if (userStore.consumables.antiquitySkillPageFragment < needFragmentCount.value) return ElMessage.warning('秘技残页不足')
  if (userStore.skillList.length >= userStore.getSkillCapacity) return ElMessage.warning('技能背包已满')
  if (!skillInfo.value) return
  const item = skillItemList.value.find((item) => item.id === skillInfo.value?.id)
  if (!item) return

  ElMessageBox.confirm(`<p class="affix">确定合成【<span class="${item.quality}">${getSkillName(item, true, 0, false)}</span>】吗？</p>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const res = await socketEmitAsync<SkillItemType>({ type: 'synthesisSkill', data: { skillId: skillInfo.value?.skillId } })
      userStore.skillList.push(res)
      synthesisResShow.value = true
      synthesisSkillInfo.value = res
      userStore.consumables.antiquitySkillPageFragment -= needFragmentCount.value
    })
    .catch(() => {})
}
</script>

<template>
  <div class="skill-collection">
    <div class="left border-wrap">
      <h4>技能列表</h4>

      <div class="item-btn-wrap btn-wrap">
        <CommonBtn
          v-for="item of skillItemList"
          :key="item.id"
          size="small"
          @done="showDetail(item)"
          :style="{ position: 'relative', borderColor: skillInfo?.id === item.id ? 'red' : '' }"
        >
          <span :class="item.quality" v-html="getSkillName(item, false, 0, false)"> </span>
        </CommonBtn>
      </div>
    </div>
    <div class="right">
      <div class="action border-wrap">
        <h4>
          操作
          <div class="help-info">
            <el-popover placement="top" width="100%" trigger="click">
              <template #reference>
                <el-icon size="16"><QuestionFilled /></el-icon>
              </template>
              <div class="affix">
                <h4>技能图鉴说明</h4>
                <p>1. 技能列表中的技能属性代表。</p>
                <p>2. 通过收集<span class="antiquity">秘技残页</span>，可进行指定技能合成。</p>
              </div>
            </el-popover>
          </div>
        </h4>

        <p style="font-size: 12px; margin: 5px 0">持有秘技残页： {{ userStore.consumables.antiquitySkillPageFragment }}</p>
        <el-button type="primary" plain size="small" :disabled="!skillInfo" @click="handleSynthesis"
          >合成<span v-show="needFragmentCount > 0">（{{ needFragmentCount }} 秘技残页）</span></el-button
        >
      </div>
      <div class="skill-info border-wrap">
        <h4>技能信息</h4>

        <template v-if="skillInfo">
          <SkillInfo :skill-info="skillInfo" :show-action="false" />
        </template>
      </div>
    </div>

    <el-dialog v-model="synthesisResShow" width="100%" style="max-width: 500px">
      <h2 class="affix">
        <span class="grow">合成成功</span>
      </h2>
      <template v-if="synthesisSkillInfo">
        <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
          <template #reference>
            <CommonBtn size="small">
              <span :class="synthesisSkillInfo.quality">{{ getSkillNameById(synthesisSkillInfo.skillId) }}</span>
            </CommonBtn>
          </template>
          <SkillInfo :skill-info="synthesisSkillInfo" :show-action="false" />
        </el-popover>
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="synthesisResShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.skill-collection {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: row nowrap;
  flex: 1;
  justify-content: space-between;
  h4 {
    margin: 10px 0;
    display: flex;
    justify-content: center;
    position: relative;
    .help-info {
      position: absolute;
      margin-left: 10px;
      right: 30%;
    }
  }
  .border-wrap {
    padding: 0 5px;
  }
  .left {
    width: calc(50% - 5px);
    margin-bottom: 10px;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-flow: column;
    .item-btn-wrap {
      overflow: auto;
      :deep() {
        .el-button:focus,
        .el-button:hover {
          background-color: unset;
        }
      }
    }
    .btn-wrap > div {
      margin-bottom: 10px;
    }
  }
  .right {
    width: calc(50% - 5px);
    display: flex;
    flex-flow: column nowrap;
    .action {
      margin-bottom: 10px;
      padding-bottom: 10px;
      .el-button {
        width: 100%;
      }
    }
    .skill-info {
      height: 100%;
    }
  }
  .radio-list {
    max-height: 50vh;
    overflow: auto;
    display: flex;
    flex-flow: column nowrap;
    .el-radio {
      width: 80%;
      margin-bottom: 10px;
      margin-right: 0;
      :deep(.el-radio__label) {
        width: 100% !important;
      }
    }
  }
}
</style>
